import React, { useState, useContext, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Container,
  Button,
  Form,
  InputGroup,
  FormControl,
  Stack,
  Row,
  Col,
  Spinner,
} from 'react-bootstrap'
import { toast } from 'react-toastify'
import AuthContext from '../../../contexts/AuthContext'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { useMsal } from '@azure/msal-react'
import { getToken, getMe } from '../../../services/opw.api'
import LoginLogo from '../../../Assets/Images/ServiceAvengerLogo.png'
import { Eye, EyeSlash } from 'react-bootstrap-icons'
import { loginRequest } from '../../../config/msalConfig'
import ForgetPasswordModal from './ForgetPasswordModal'
import { IApiError } from '../../../types/apiError'
import { useMicrosoftSession } from '../../../helpers/useMicrosoftSession'

const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup.string().required('Password is required'),
})

const LoginForm = () => {
  const authContext = useContext(AuthContext)
  const { instance } = useMsal()
  const { completeMicrosoftLogin } = useMicrosoftSession()
  const navigate = useNavigate()
  const location = useLocation()

  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [msalLoading, setMsalLoading] = useState(false)
  const [showForgetPasswordModal, setShowForgetPasswordModal] = useState(false)

  const handleCloseForgetPasswordModal = () => setShowForgetPasswordModal(false)
  const handleShowForgetPasswordModal = () => setShowForgetPasswordModal(true)
  const handleClickShowPassword = () => setShowPassword((show) => !show)

  // Shared with the redirect path, which lands on /redirect and has to acquire its
  // own token before exchanging it.
  const loginMicrosoft = async (data: { token: string }) => {
    try {
      await completeMicrosoftLogin(data.token)
    } finally {
      setMsalLoading(false)
    }
  }

  const microsoftHandler = async () => {
    if (msalLoading) return
    setMsalLoading(true)

    try {
      const response = await instance.loginPopup({
        ...loginRequest,
        prompt: 'select_account',
      })

      if (response && response.accessToken) {
        await loginMicrosoft({ token: response.accessToken })
      } else {
        setMsalLoading(false)
      }
    } catch (e) {
      console.error('MSAL Popup Error:', e)
      const msalError = e as { errorCode?: string; errorMessage?: string; message?: string }
      if (
        msalError.errorCode === 'popup_window_error' ||
        msalError.errorMessage?.includes('popup') ||
        msalError.errorMessage?.includes('interaction_in_progress')
      ) {
        console.warn('Popup blocked or interaction in progress. Switching to Redirect mode.')
        instance.loginRedirect(loginRequest)
      } else {
        toast.error(`Microsoft Auth Error: ${msalError.message}`)
        setMsalLoading(false)
      }
    }
  }

  const loginUser = async (values: { email: string; password: string }) => {
    setLoading(true)
    try {
      const token = await getToken(values?.email, values?.password)
      if (token) {
        const userData = await getMe(token)
        if (userData?.data?.user) {
          authContext.updateData('user', userData.data.user)
          authContext.updateData('token', token)
          toast.success('You successfully logged in', {
            position: 'top-right',
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
          })
        }
      }
    } catch (err) {
      console.log(
        (err as IApiError)?.response?.data?.message ||
          (err as IApiError)?.message ||
          'Login Failed!',
      )
    } finally {
      setLoading(false)
    }
  }

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      loginUser(values)
    },
  })

  useEffect(() => {
    if (location.pathname === '/login' && authContext?.data?.token) {
      navigate('/')
    }
  }, [authContext?.data?.token, navigate, location.pathname])

  return (
    <Container className='d-flex justify-content-center align-items-center py-4'>
      <Row className='p-4 shadow bg-white rounded' style={{ maxWidth: '450px', width: '100%' }}>
        <Form onSubmit={formik.handleSubmit}>
          <Row className='justify-content-center mb-4'>
            <Col xs='auto'>
              <img src={LoginLogo} alt='login logo' height='40' />
            </Col>
          </Row>

          <Row className='justify-content-center mb-4'>
            <Col xs={'auto'}>
              <Button onClick={microsoftHandler} variant='outline-dark' disabled={msalLoading}>
                Sign in with Microsoft {msalLoading && <Spinner size='sm' />}
              </Button>
            </Col>
          </Row>
          <Row className='justify-content-center'>
            <Col xs='auto'>
              <hr className='w-100' />
            </Col>
            <Col xs='auto'>
              <span>OR</span>
            </Col>
            <Col xs='auto'>
              <hr className='w-100' />
            </Col>
          </Row>
          <Row className='justify-content-center mb-4 text-center'>
            <span>Sign in with Email address</span>
          </Row>
          <Stack>
            <FormControl
              id='email'
              name='email'
              placeholder='Email'
              value={formik.values.email}
              onChange={formik.handleChange}
              isInvalid={formik.touched.email && !!formik.errors.email}
              style={{ minWidth: '300px' }}
            />
            {formik.touched.email && formik.errors.email && (
              <Form.Text className='text-danger'>{formik.errors.email}</Form.Text>
            )}
            <InputGroup className='mt-2'>
              <FormControl
                id='password'
                name='password'
                placeholder='Password'
                type={!showPassword ? 'password' : 'text'}
                value={formik.values.password}
                onChange={formik.handleChange}
                isInvalid={formik.touched.password && !!formik.errors.password}
              />
              <Button
                variant='outline-dark'
                onClick={handleClickShowPassword}
                onMouseDown={(e) => e.preventDefault()}
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </Button>
            </InputGroup>
            {formik.touched.password && formik.errors.password && (
              <Form.Text className='text-danger'>{formik.errors.password}</Form.Text>
            )}
            <Row className='justify-content-md-center mt-3'>
              <Col xs={'auto'}>
                <Button
                  className='mt-3'
                  variant='dark'
                  type='submit'
                  style={{ width: '150px' }}
                  disabled={loading}
                >
                  Login {loading && <Spinner size='sm' />}
                </Button>
              </Col>
            </Row>
            <Button variant='link' onClick={handleShowForgetPasswordModal}>
              Forgot Password?
            </Button>
            <div className='text-center'>
              <a href='mailto:serviceportal@onpointwarranty.com?subject=Requesting%20a%20dealer%20portal%20account'>
                Request Account
              </a>
            </div>
            <ForgetPasswordModal
              show={showForgetPasswordModal}
              handleClose={handleCloseForgetPasswordModal}
            />
          </Stack>
        </Form>
      </Row>

      <ForgetPasswordModal
        show={showForgetPasswordModal}
        handleClose={handleCloseForgetPasswordModal}
      />
    </Container>
  )
}

export default LoginForm
